import { useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useEffectOnce } from "react-use"

import { ATTIO_REQUIRED_PLANS } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	AttioIcon,
	Badge,
	Button,
	IntegrationTemplate,
	Loader,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useSession } from "@/lib/session"
import { PlanPill } from "../plan-pill"
import { IntegrationLayout } from "./integration-layout"

const ATTIO_TITLE = "Attio"
const ATTIO_DESCRIPTION =
	"Log daily support activity to the matching person and company in Attio."

const ATTIO_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How the Attio integration works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				Once a day, Productlane writes a note to Attio for each thread that had
				new messages, on the matching person and company. The note lists that
				day&apos;s messages and links back to the full thread, so your sales and
				success teams see the current support relationship without leaving
				Attio.
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Setup instructions
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">1.</span>
					<span>
						Click &quot;Connect&quot; to authorize Productlane to access Attio.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">2.</span>
					<span>
						Threads with a linked contact or company are matched to a person by
						email, or a company by domain.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">3.</span>
					<span>
						Each day, a note with that day&apos;s messages and a thread link is
						added to the record.
					</span>
				</div>
			</div>
		</div>
	</div>
)

export function AttioIntegrationPage() {
	const workspaceQuery = api.workspace.info.useQuery()

	return (
		<>
			{workspaceQuery.data ? (
				<AttioForm data={{ workspace: workspaceQuery.data }} />
			) : (
				<div className="flex justify-center">
					<Loader />
				</div>
			)}
		</>
	)
}

interface AttioFormProps {
	data: {
		workspace: RouterOutputs["workspace"]["info"]
	}
}

function AttioForm({ data }: AttioFormProps) {
	const ctx = api.useUtils()
	const navigate = useNavigate()
	const isSuccess = useSearch({ strict: false, select: (s) => s.success })

	const [dialogOpen, setDialogOpen] = useState(false)
	const { session } = useSession()

	useEffectOnce(() => {
		if (isSuccess === "true") {
			toast.success({
				title: "Success",
				description: "Attio is now connected",
			})
			void navigate({ to: "/settings/integrations/attio", replace: true })
		} else if (isSuccess === "false") {
			toast.error({
				title: "Something went wrong",
				description: "Feel free to try again or contact us.",
			})
			void navigate({ to: "/settings/integrations/attio", replace: true })
		}
	})

	const disconnectMutation = api.workspace.disconnectAttio.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})
	const { passes: hasRequiredPlan } = useRequiredPlans(ATTIO_REQUIRED_PLANS)
	const connected = data.workspace.attioConnected
	const [isLoading, setIsLoading] = useState(false)

	return (
		<>
			<MetaInfo title="Attio Integration" />
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<AttioIcon className="w-10" />}
				name={
					<div className="inline-flex items-center gap-4">
						{ATTIO_TITLE}
						{connected ? (
							<Badge variant="green">Connected</Badge>
						) : (
							<PlanPill plan="scale" />
						)}
					</div>
				}
				description={ATTIO_DESCRIPTION}
				instructions={ATTIO_LONG_DESCRIPTION}
				button={
					hasRequiredPlan ? (
						connected ? (
							<Button
								isLoading={disconnectMutation.isLoading}
								onClick={() => {
									if (session?.membershipRole === "ADMIN") {
										setDialogOpen(true)
										return
									}
									toast.info({
										title: "Action not allowed",
										description:
											"Only admins have permission to disconnect integrations.",
										action: {
											label: "View your role",
											callback: () => {
												void navigate({ to: "/settings/members" })
											},
										},
									})
								}}
								disabled={session?.membershipRole !== "ADMIN"}
							>
								Disconnect
							</Button>
						) : (
							<RoleCheck not={["VIEWER"]}>
								<Button
									isLoading={isLoading}
									onClick={() => {
										setIsLoading(true)
										analytics.track("connect-attio")
										window.location.href = `${env.VITE_API_URL}/api/attio/connect`
									}}
								>
									Connect
								</Button>
							</RoleCheck>
						)
					) : (
						<UpgradeButton required={ATTIO_REQUIRED_PLANS} />
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Attio Integration?"
				description="Are you sure you want to disconnect Attio? Existing notes in Attio remain, but new messages will no longer sync."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={() => {
					analytics.track("disconnect-attio")
					disconnectMutation.mutate()
					setDialogOpen(false)
				}}
			/>
		</>
	)
}
